import React, {useRef, useState} from 'react';
import {Images, PRE_SIGNUP_FILES_KEY, SUCCESS_KEY} from '@/constants';
import {Icon, Button, Modal, Text} from '@/ui-kits';
import {useToast} from '@/hooks';
import {useNavigate} from 'react-router-dom';
import {getErrorMessage, getSuccessMessage} from '@/utils/getMessages';
import {Routes} from '@/navigation/Routes';
import {uploadHealthReportPublic} from '@/services/api';
import {savePreSignupFilesToken} from '@/utils/common_functions';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
}
export const UploadModal: React.FC<UploadModalProps> = props => {
  const {showToast} = useToast();
  const navigate = useNavigate();
  const {open, onClose} = props;
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const allowedFormat = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsLoading(true);
      const files = e.target.files ?? [];
      
      if (files.length === 0) {
        setIsLoading(false);
        return;
      }
      for (const file of files) {
        const isAllowed = allowedFormat.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!isAllowed) {
          showToast(getErrorMessage('E-10016'), 'failure');
          setIsLoading(false);
          if (inputRef?.current) {
            inputRef.current.value = '';
          }
          return;
        }
      }

      // add the payload
      const payload = new FormData();
      for (const file of files) {
        payload.append('file', file);
      }

      // call the api
      const response = await uploadHealthReportPublic(payload);

      // Check if the response indicates success
      if (response?.data?.status === SUCCESS_KEY || response?.status === 200 || (response?.data && !response?.data?.error)) {
        // save the files to the local storage - token might be in different locations
        const token = response?.data?.data?.token || response?.data?.token || response?.data?.data?.pre_signup_token;

        if (token) {
          savePreSignupFilesToken(token);
        }

        showToast(getSuccessMessage('S-10020'), 'success');
        setIsLoading(false);
        onClose();
        if (inputRef?.current){
          inputRef.current.value = '';
        }
        // Navigate to signup after successful upload
        navigate(Routes.SIGNUP);
      } else {
        showToast(getErrorMessage('E-10001'), 'failure');
        setIsLoading(false);
        if (inputRef?.current){
          inputRef.current.value = '';
        }
      }
    } catch (error) {
      setIsLoading(false);
      showToast(getErrorMessage('E-10001'), 'failure');
      if (inputRef?.current){
        inputRef.current.value = '';
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="p-0 bg-transparent w-fit max-w-[calc(100%-2rem)] mx-auto">
      <div className="bg-cover bg-center flex relative flex-col p-8 sm:p-10 rounded-2xl overflow-hidden" style={{backgroundImage: `url(${Images.bg_hero})`}}>
        <button onClick={onClose} className="absolute top-2 right-2 sm:top-4 sm:right-3 outline-none z-5">
          <Icon className="size-[24px]" type="close" />
        </button>

        <div className="bg-white items-center rounded-lg flex flex-col gap-4 w-full max-w-[400px] overflow-y-auto p-6 sm:p-8">
          <Text className="text-xl font-headingBold text-ui_blue text-center">
            Please upload documents for one medical condition at a time. You can select multiple files together
          </Text>
          <Text className="font-Label text-sm gap-1 inline-flex text-ui_input_border_dark">
            <span className="text-rejection">* </span>File supported : pdf, jpg, png, doc, docx
          </Text>

          <label style={{width: '100%'}} htmlFor="upload-health-records">
            <input
              accept={allowedFormat.join(',')}
              ref={inputRef}
              id="upload-health-records"
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{display: 'none'}}
            />

            <Button
              title={isLoading ? 'Uploading...' : 'Add Document'}
              leftIcon="arrow_up_circle_white"
              className="w-full"
              isLoading={isLoading}
              disabled={isLoading}
              onClick={() => document.getElementById('upload-health-records')?.click()}
            />
          </label>
        </div>
      </div>
    </Modal>
  );
};
